import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import classNames from "classnames";
import type { MouseEvent } from "react";
import { SegmentedPills } from "../primitives";
import type { PillOption } from "../primitives";
import CustomizeViewMenu, { BlockToggle } from "../CustomizeViewMenu";
import styles from "./style.module.scss";

export type RangePreset = string | number;

/** Today as `YYYY-MM-DD` in the viewer's own timezone, not UTC. */
function today() {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
}

export interface CustomRange {
  from: string;
  to: string;
}

interface RangeFilterBarProps {
  presets: PillOption<RangePreset>[];
  preset: RangePreset;
  onPresetChange: (preset: RangePreset) => void;
  /** Rendered only while the active preset is the custom one. */
  showCustomRange?: boolean;
  range?: CustomRange;
  onRangeChange?: (range: CustomRange) => void;
  blocks?: BlockToggle[];
  customizeOpen?: boolean;
  onCustomizeOpenChange?: (open: boolean) => void;
  onToggleBlock?: (key: string, visible: boolean) => void;
  className?: string;
}

/** Date presets, the custom date pair and the customize-view dropdown. */
function RangeFilterBar({
  presets,
  preset,
  onPresetChange,
  showCustomRange,
  range,
  onRangeChange,
  blocks,
  customizeOpen = false,
  onCustomizeOpenChange,
  onToggleBlock,
  className,
}: RangeFilterBarProps) {
  /* The native picker only opens from the input's own indicator, which we hide
     in favour of the single pill icon - open it from anywhere in the field. */
  const openPicker = (event: MouseEvent<HTMLInputElement>) => {
    event.currentTarget.showPicker?.();
  };

  /* Analytics only has data up to today - keep the picker off future dates. */
  const latest = today();

  return (
    <div className={classNames(styles.bar, className)}>
      <SegmentedPills
        options={presets}
        value={preset}
        onChange={onPresetChange}
      />

      {showCustomRange && range && (
        <div className={styles.range}>
          <CalendarTodayIcon className={styles.rangeIcon} />
          <input
            type="date"
            aria-label="Range start"
            value={range.from}
            max={range.to || latest}
            className={styles.date}
            onClick={openPicker}
            onChange={(event) =>
              onRangeChange?.({ ...range, from: event.target.value })
            }
          />
          <ArrowForwardIcon className={styles.rangeArrow} />
          <input
            type="date"
            aria-label="Range end"
            value={range.to}
            min={range.from}
            max={latest}
            className={styles.date}
            onClick={openPicker}
            onChange={(event) =>
              onRangeChange?.({ ...range, to: event.target.value })
            }
          />
        </div>
      )}

      {blocks && blocks.length > 0 && (
        <CustomizeViewMenu
          blocks={blocks}
          open={customizeOpen}
          onToggleOpen={(open) => onCustomizeOpenChange?.(open)}
          onToggleBlock={(key, visible) => onToggleBlock?.(key, visible)}
        />
      )}
    </div>
  );
}

export default RangeFilterBar;
